import { POINT_FACES } from '../../elements/theme';

// =============================================================================
// SCHEMA TYPES
// =============================================================================

export type StyleControlType = 'color' | 'number' | 'toggle' | 'select';

export interface StylePropertySchema {
  readonly key: string;
  readonly label: string;
  readonly control: StyleControlType;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly options?: ReadonlyArray<{ label: string; value: string }>;
}

/** Keys matching the FeatureStyleNode / GeometryElementDisplayConfig style sub-objects */
export type StyleKeyType = 'point' | 'line' | 'polygon' | 'angle' | 'circle' | 'curve';

export interface StyleTypeSchema {
  readonly key: StyleKeyType;
  readonly label: string;
  readonly properties: readonly StylePropertySchema[];
}

/** Describes one collapsible section in the style editor. */
export interface StyleSectionDescriptor {
  /** Section heading, e.g. "Vertices", "Edges", "Tangent Line" */
  readonly name: string;
  /** Which style type this section configures */
  readonly styleKey: StyleKeyType;
  /** Whether to show the label sub-editor for this section */
  readonly showLabel?: boolean;
  /** Whether to show a visible toggle in the section header */
  readonly showVisible?: boolean;
  /** Subpart path for nested style targeting (graphpad FeatureStyleNode.subparts) */
  readonly subpartPath?: readonly string[];
  /** Default values for this section's properties, shown as placeholders when no value is set */
  readonly defaults?: Readonly<Record<string, unknown>>;
}

// =============================================================================
// OPTION SETS
// =============================================================================

const POINT_FACE_OPTIONS: ReadonlyArray<{ label: string; value: string }> = POINT_FACES.map((f) => ({
  label: f,
  value: f,
}));

const ANGLE_TYPE_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Sector', value: 'sector' },
  { label: 'Square', value: 'square' },
  { label: 'None', value: 'none' },
];

// =============================================================================
// STYLE TYPE SCHEMAS
// =============================================================================

export const POINT_STYLE_SCHEMA: StyleTypeSchema = {
  key: 'point',
  label: 'Point',
  properties: [
    { key: 'color', label: 'Color', control: 'color' },
    { key: 'size', label: 'Size', control: 'number', min: 1, max: 20, step: 1 },
    { key: 'face', label: 'Face', control: 'select', options: POINT_FACE_OPTIONS },
    { key: 'strokeColor', label: 'Stroke Color', control: 'color' },
    { key: 'fillOpacity', label: 'Opacity', control: 'number', min: 0, max: 1, step: 0.05 },
    { key: 'strokeWidth', label: 'Stroke W', control: 'number', min: 0, max: 10, step: 0.5 },
    { key: 'fixed', label: 'Fixed', control: 'toggle' },
    { key: 'showGrabHandle', label: 'Grab Handle', control: 'toggle' },
  ],
};

export const LINE_STYLE_SCHEMA: StyleTypeSchema = {
  key: 'line',
  label: 'Line',
  properties: [
    { key: 'color', label: 'Color', control: 'color' },
    { key: 'strokeWidth', label: 'Stroke', control: 'number', min: 0.5, max: 10, step: 0.5 },
    { key: 'dash', label: 'Dash', control: 'number', min: 0, max: 10, step: 1 },
  ],
};

export const POLYGON_STYLE_SCHEMA: StyleTypeSchema = {
  key: 'polygon',
  label: 'Polygon',
  properties: [
    { key: 'fillColor', label: 'Fill Color', control: 'color' },
    { key: 'fillOpacity', label: 'Opacity', control: 'number', min: 0, max: 1, step: 0.1 },
    { key: 'strokeColor', label: 'Stroke Color', control: 'color' },
    { key: 'strokeWidth', label: 'Stroke W', control: 'number', min: 0, max: 10, step: 0.5 },
  ],
};

export const ANGLE_STYLE_SCHEMA: StyleTypeSchema = {
  key: 'angle',
  label: 'Angle',
  properties: [
    { key: 'fillColor', label: 'Fill Color', control: 'color' },
    { key: 'fillOpacity', label: 'Opacity', control: 'number', min: 0, max: 1, step: 0.1 },
    { key: 'strokeColor', label: 'Stroke Color', control: 'color' },
    { key: 'radius', label: 'Radius', control: 'number', min: 0.1, max: 5, step: 0.1 },
    { key: 'type', label: 'Type', control: 'select', options: ANGLE_TYPE_OPTIONS },
  ],
};

export const CIRCLE_STYLE_SCHEMA: StyleTypeSchema = {
  key: 'circle',
  label: 'Circle',
  properties: [
    { key: 'strokeColor', label: 'Color', control: 'color' },
    { key: 'fillColor', label: 'Fill Color', control: 'color' },
    { key: 'fillOpacity', label: 'Opacity', control: 'number', min: 0, max: 1, step: 0.1 },
    { key: 'strokeWidth', label: 'Stroke', control: 'number', min: 0.5, max: 10, step: 0.5 },
    { key: 'dash', label: 'Dash', control: 'number', min: 0, max: 10, step: 1 },
  ],
};

export const CURVE_STYLE_SCHEMA: StyleTypeSchema = {
  key: 'curve',
  label: 'Curve',
  properties: [
    { key: 'color', label: 'Color', control: 'color' },
    { key: 'strokeWidth', label: 'Stroke', control: 'number', min: 0.5, max: 10, step: 0.5 },
    { key: 'dash', label: 'Dash', control: 'number', min: 0, max: 10, step: 1 },
  ],
};

/** Lookup schema by style key */
export const STYLE_SCHEMAS: Record<StyleKeyType, StyleTypeSchema> = {
  point: POINT_STYLE_SCHEMA,
  line: LINE_STYLE_SCHEMA,
  polygon: POLYGON_STYLE_SCHEMA,
  angle: ANGLE_STYLE_SCHEMA,
  circle: CIRCLE_STYLE_SCHEMA,
  curve: CURVE_STYLE_SCHEMA,
};
